import { z } from 'zod'

import {
  bytesSchema,
  isoDateSchema,
  moneyPaisa,
  paisaSchema,
  ratioSchema,
} from '../common'

/**
 * P1 · `GET /platform/overview` — Platform screen 01.
 *
 * A note that runs through the whole platform surface: the console sees
 * **counts and its own revenue**, and only the aggregate value of the books it
 * hosts. It never reads a trader's animals, prices or profit for its own
 * screens; to see inside a book it must open one through P5, which writes an
 * `access_log` row with a reason first.
 *
 * The money here is the platform's own, so it is not subject to a tenant's
 * price lock — but it stays optional like every money key, because a
 * `PLATFORM_SUPPORT` actor sees usage and health without seeing revenue.
 */
export const platformOverviewResponse = z.object({
  today: isoDateSchema,
  monthlyRevenuePaisa: moneyPaisa(),
  /** Change against last month. Signed — revenue goes down too. */
  revenueDeltaPaisa: moneyPaisa(),
  payingCount: z.number().int().nonnegative(),
  averagePerBusinessPaisa: moneyPaisa(),
  trialCount: z.number().int().nonnegative(),
  totalBusinesses: z.number().int().nonnegative(),
  newThisMonth: z.number().int().nonnegative(),
  cancelledThisMonth: z.number().int().nonnegative(),
  /** Trials converted, as the console phrases it ("7 of 11"). */
  trialsConverted: z.string(),
  animalsTracked: z.number().int().nonnegative(),
  /** Aggregate cost basis across every hosted book. A total, never a book. */
  cattleUnderManagementPaisa: moneyPaisa(),
  profitBookedPaisa: moneyPaisa(),
  /** Platform revenue as a share of trader profit. A ratio, not money. */
  cutOfProfit: ratioSchema.optional(),
  pastDueCount: z.number().int().nonnegative(),
  pastDueAmountPaisa: moneyPaisa(),
  pastDueShare: ratioSchema.optional(),
  payingByMonth: z.array(
    z.object({ month: z.string(), count: z.number().int().nonnegative() }),
  ),
  storageUsedBytes: bytesSchema,
  storageLimitBytes: bytesSchema.nullable(),
  backupsDone: z.number().int().nonnegative(),
  backupsTotal: z.number().int().nonnegative(),
  videoQueue: z.number().int().nonnegative(),
  videoFailed24h: z.number().int().nonnegative(),
})

/** Kept explicit so a reader sees revenue deltas may legitimately be negative. */
export const signedPlatformPaisa = paisaSchema

export type PlatformOverviewResponse = z.infer<typeof platformOverviewResponse>
